"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Lang } from "@/lib/i18n";
import { DICT, getDict } from "@/lib/i18n";

const STORAGE_KEY = "plumtown-lang";

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  toggle: () => void;
  t: ReturnType<typeof getDict>;
};

const LangContext = createContext<Ctx>({
  lang: "jp",
  setLang: () => {},
  toggle: () => {},
  t: DICT.jp,
});

export function useLang(): Ctx {
  return useContext(LangContext);
}

export function LangProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>("jp");

  // Hydrate from localStorage after mount
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === "jp" || stored === "en") {
        setLangState(stored);
        document.documentElement.lang = stored === "en" ? "en" : "ja";
      }
    } catch {
      /* ignore */
    }
  }, []);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
      document.documentElement.lang = l === "en" ? "en" : "ja";
    } catch {
      /* ignore */
    }
  }, []);

  const toggle = useCallback(() => {
    setLang(lang === "jp" ? "en" : "jp");
  }, [lang, setLang]);

  const value = useMemo<Ctx>(
    () => ({ lang, setLang, toggle, t: getDict(lang) }),
    [lang, setLang, toggle]
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}
